import React, { useState, useEffect } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { Spinner } from '../../../../catalyst-components/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { formatDateWithTime } from '../../../../shared/helpers';
import { ArrowLeftIcon, EyeIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { AssistantCards } from './AssistantCards';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const AllSessionsTab: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [cursor, setCursor] = useState<string | null>(null);
  const [pageHistory, setPageHistory] = useState<Array<{ page: number; cursor: string | null }>>([]);
  const [loading, setLoading] = useState(false);

  const paginatedResult = useQuery(api.sessions.getCompletedSessionsPaginated, {
    paginationOpts: {
      numItems: pageSize,
      cursor: cursor,
    }
  });

  useEffect(() => {
    if (paginatedResult && currentPage === 1 && pageHistory.length === 0) {
      setPageHistory([{ page: 1, cursor: null }]);
    }
  }, [paginatedResult, currentPage, pageHistory.length]);

  const goToPage = (targetPage: number) => {
    if (targetPage === currentPage) return;
    setLoading(true);
    if (targetPage > currentPage) {
      if (paginatedResult?.nextCursor) {
        setCursor(paginatedResult.nextCursor);
        setPageHistory(prev => [...prev, { page: targetPage, cursor: paginatedResult.nextCursor }]);
      }
    } else {
      const targetPageData = pageHistory.find(p => p.page === targetPage);
      if (targetPageData) {
        setCursor(targetPageData.cursor);
      } else {
        setCursor(null);
        setPageHistory([{ page: 1, cursor: null }]);
      }
    }
    setCurrentPage(targetPage);
  };

  const goToNextPage = () => {
    if (paginatedResult?.nextCursor) {
      goToPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToFirstPage = () => {
    goToPage(1);
  };

  useEffect(() => {
    if (paginatedResult) {
      setLoading(false);
    }
  }, [paginatedResult]);

  const formatDuration = (durationMs: number) => {
    if (durationMs === 0) return 'N/A';
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  if (!paginatedResult && currentPage === 1) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  const sessions = paginatedResult?.sessions || [];
  const hasNextPage = paginatedResult?.nextCursor && !paginatedResult?.isDone;
  const hasPreviousPage = currentPage > 1;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
          <span className="text-sm text-muted-foreground">Total Sessions: </span>
          <span className="font-semibold text-foreground">{paginatedResult?.totalCount || 0}</span>
        </div>
        <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
          <span className="text-sm text-muted-foreground">Page </span>
          <span className="font-semibold text-foreground">{currentPage}</span>
          <span className="text-sm text-muted-foreground"> of </span>
          <span className="font-semibold text-foreground">{paginatedResult?.totalCount ? Math.ceil(paginatedResult.totalCount / pageSize) : '?'}</span>
        </div>
        <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
          <span className="text-sm text-muted-foreground">On this page: </span>
          <span className="font-semibold text-foreground">{sessions.length}</span>
        </div>
        {paginatedResult?.isDone && (
          <div className="px-4 py-2 bg-green-50 border-2 border-green-200 rounded-full">
            <span className="text-sm font-medium text-green-700">End of data</span>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
        <Table>
          <TableHead>
            <TableRow className="bg-muted/30">
              <TableHeader>Assistant Name</TableHeader>
              <TableHeader>Start Date</TableHeader>
              <TableHeader>Duration</TableHeader>
              <TableHeader>User Name</TableHeader>
              <TableHeader>User Email</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.session_id} className="hover:bg-muted/20">
                <TableCell>
                  <span className="text-sm font-medium text-foreground">{session.assistant_name}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">{formatDateWithTime(session.creation_time)}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">{formatDuration(session.duration)}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm font-medium text-foreground">{session.user_name}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">{session.user_email}</span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      color="secondary"
                      className="rounded-full"
                      onClick={() => navigate(`/chat?sessionId=${session.session_id}`)}
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      color="primary"
                      className="rounded-full"
                      onClick={() => navigate(`/admin/sessions/${session.session_id}/insights?from=sessions&userId=${session.user_id}`)}
                    >
                      <ChartBarIcon className="h-4 w-4 mr-1" />
                      Insights
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-center items-center gap-3">
        <Button color="secondary" className="rounded-full" onClick={goToFirstPage} disabled={!hasPreviousPage || loading}>
          First
        </Button>
        <Button color="secondary" className="rounded-full" onClick={goToPreviousPage} disabled={!hasPreviousPage || loading}>
          Previous
        </Button>
        <span className="px-4 py-2 text-sm text-muted-foreground">
          Page {currentPage} of {paginatedResult?.totalCount ? Math.ceil(paginatedResult.totalCount / pageSize) : '?'}
        </span>
        <Button color="secondary" className="rounded-full" onClick={goToNextPage} disabled={!hasNextPage || loading}>
          Next
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center">
          <Spinner className="w-6 h-6" />
        </div>
      )}

      {sessions.length === 0 && !loading && (
        <div className="text-center py-12 text-muted-foreground bg-muted/30 rounded-xl">
          No completed sessions found on this page
        </div>
      )}
    </div>
  );
};

export const SessionAnalytics = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/admin');
  };

  const tabs = [
    { name: 'Assistants' },
    { name: 'All Sessions' },
  ];

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button color="secondary" className="rounded-full" onClick={handleBackClick}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white border-2 border-green-200">
              <ChartBarIcon className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-1">Session Analytics</h1>
              <p className="text-base text-muted-foreground">
                View and analyze user sessions
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div>
          <Tab.Group>
            <Tab.List className="flex space-x-1 rounded-xl bg-muted/50 p-1 mb-6">
              {tabs.map((tab) => (
                <Tab
                  key={tab.name}
                  className={({ selected }) =>
                    classNames(
                      'w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all',
                      'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2',
                      selected
                        ? 'bg-white text-foreground shadow'
                        : 'text-muted-foreground hover:bg-white/50 hover:text-foreground'
                    )
                  }
                >
                  {tab.name}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <AssistantCards />
              </Tab.Panel>
              <Tab.Panel>
                <AllSessionsTab />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    </div>
  );
};
